import _ from '@lodash';
import Avatar from '@mui/material/Avatar';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Slide from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserToMessage, setUserToMessage, createExternalMessage} from 'app/store/externalMessageSlice';
import { useForm } from 'react-hook-form';
import { dispatchResultMessage } from '../utils';
import ControlledTextField from '../form/ControlledTextField';
import DefaultButton from '../layout/DefaultButton';

// The public endpoint records every request it rejects, and refuses a second message to the same
// provider from the same caller for 8 hours. A request the server would reject is therefore stopped
// HERE, with a visible reason, so a visitor who makes a mistake is not locked out of retrying. That
// covers the length limits (100 name, 5000 message, 30 phone: the backend's, counted on the raw value
// in UTF-16 code units like yup's .max), a blank required value, and an email the server's pattern
// (ValidationUtils.EMAIL_PATTERN) would refuse. Keep all of these in step with the backend.
export const MESSAGE_LIMITS = { name: 100, message: 5000, phone: 30 };

const tooLong = (what, max) => `${what} must be ${max} characters or fewer.`;

// The backend's email pattern, verbatim. yup's own .email() is looser (it accepts a 65-character
// local part, `me@localhost` and `a@b`, which the server rejects).
export const SERVER_EMAIL_PATTERN = /^(?=.{1,64}@)[A-Za-z0-9_!#$%&'*+=?^`{|}~-]+(\.[A-Za-z0-9_!#$%&'*+=?^`{|}~-]+)*@[^-][A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]{2,})$/;

// .trim() so a whitespace-only value counts as blank (the server rejects it) and stray spaces are not sent.
export const schema = yup.object().shape({
  name: yup.string().trim().required('You must enter a name').max(MESSAGE_LIMITS.name, tooLong('Your name', MESSAGE_LIMITS.name)),
  email: yup.string().trim().required('You must enter an email').test('server-email', 'You must enter a valid email.', (value) => SERVER_EMAIL_PATTERN.test(value || '')),
  message: yup.string().trim().required('You must enter a message').max(MESSAGE_LIMITS.message, tooLong('The message', MESSAGE_LIMITS.message)),
  phone: yup.string().trim().max(MESSAGE_LIMITS.phone, tooLong('The phone number', MESSAGE_LIMITS.phone)),
});

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function ExternalMessageDialog(props) {
  const dispatch = useDispatch();  
  const target = useSelector(selectUserToMessage);
  const { id, name, profileImageUrl} = target;
  const title = 'Send Message to ' + name;
  
  const { handleSubmit, formState, control } = useForm({
    mode: 'onChange',
    defaultValues: {
      message: '',
    },
    resolver: yupResolver(schema),
  });

  const { isValid, dirtyFields, errors } = formState;
  

  function handleCloseDialog() {
    
  }

  function handleDiscard() {
	  dispatch(setUserToMessage({}));
  }

  function onSubmit(data) {
    dispatch(createExternalMessage({targetId: id, name: data.name, email: data.email, message: data.message})).then((resp) =>
	{
		dispatchResultMessage(dispatch, resp, "Sent Message!");
	});
	dispatch(setUserToMessage({}));
  }
 

  return (
    <Dialog
      classes={{
        paper: 'w-full m-24',
      }}
      TransitionComponent={Transition}
      onClose={handleCloseDialog}
      open={Boolean(id)}
    >
      <AppBar position="static" color="secondary" elevation={0}>
	      <Toolbar className="flex w-full">
	        <Avatar src={profileImageUrl} />
	        <Typography className='ml-20' variant="subtitle1" color="inherit">
	          {title}
	        </Typography>
	      </Toolbar>
	  </AppBar>
	  
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
          <DialogContent classes={{ root: 'p-16 pb-0 sm:p-32 sm:pb-0' }}>
			  <ControlledTextField name="name" label="Your Name"
                             control={control} errors={errors}
             				 autoFocus required fullWidth />
              <ControlledTextField name="email" label="Your Email"
                             control={control} errors={errors}
             				 required fullWidth />
              <ControlledTextField name="message" label="Message"
                             control={control} errors={errors}
             				 required fullWidth multiline
             				 minRows={4} maxRows={8} />

          </DialogContent>

          <DialogActions className="flex flex-col sm:flex-row sm:items-center justify-between py-16 sm:py-24 px-24">
            <div className="flex items-center space-x-8 mt-16 sm:mt-0">
              <DefaultButton text="Cancel" onClick={handleDiscard} />
              <DefaultButton text="Send"
	              disabled={_.isEmpty(dirtyFields) || !isValid}
	              submit />
            </div>
          </DialogActions>
        </form>
        
    </Dialog>
  );
}

export default ExternalMessageDialog;
